import React, { useEffect, useState, useContext } from "react"

import * as Commands from "../../../../model/commands"
import { ID, Idea } from "../../../../model/types"
import ShowSource from "./idea_pane/show_source"
import ShowEditor from "./idea_pane/show_editor"
import Connections from "./idea_pane/connections"
import Form from "./idea_pane/form"
import classNames from "classnames"
import { useNana } from "../../../hooks/useNana"
import { DeviceContext } from "../../../providers/device_context"
import Hub from "../../../../hub"
import WithinProjects from "./idea_pane/within_projects"
import { MosaicPageState, NavigationHistoryState } from "../mosaic"

const IdeaPane = ({
  ideaId,
  selectionPos,
  setOpenIdeaId,
  plateViewDraggingHappening,
}: {
  ideaId: ID
  selectionPos: number | null
  setOpenIdeaId: (id: ID | null) => void
  plateViewDraggingHappening: boolean
}) => {
  const { model, hub, commander, plateView } = useNana()
  const { isMobile } = useContext(DeviceContext)
  const idea = model.getIdea(ideaId)
  const [_tick, setTick] = useState(0)

  const deleteAndClose = () => {
    if (window.confirm("Are you sure?")) {
      setOpenIdeaId(null)
      if (ideaId)
        commander.exec(new Commands.DeleteIdeasCommand({ ids: [ideaId] }))
    }
  }

  // Check if the idea is visible, and scroll onscreen if not
  useEffect(() => {
    if (!plateView) return
    if (isMobile) return
    if (!idea) return

    const t = setTimeout(() => {
      if (!plateView.isIdeaVisible(idea.id)) {
        // reload in case its location has changed
        const idea1 = model.getIdea(idea.id)
        if (idea1) plateView.scrollIntoView(idea1.location)
      }
    }, 100)
    return () => clearTimeout(t)
  }, [idea, plateView])

  useEffect(() => {
    const keys = [
      hub.on("model:idea-updated", ({ id, originator }) => {
        if (id == ideaId && originator != "editor") setTick((tick) => tick + 1)
      }),
    ]
    return () => keys.forEach((k) => hub.remove(k))
  }, [])

  if (!ideaId) return null
  if (!idea) return null

  return (
    <div
      id="idea-pane"
      className="pointer-events-none absolute bottom-0 right-0 top-16 w-full p-2 md:w-184 md:p-8 md:pt-5 md:pb-6 md:pr-6"
      data-testid="idea-pane"
    >
      <div
        id="visible-idea"
        className={classNames(
          "flex max-h-full flex-col flex-nowrap overflow-hidden rounded-lg",
          "border border-stone-200/80 bg-[#faf9f6]",
          "shadow-[0_1px_2px_rgba(15,23,42,0.05),0_16px_48px_-12px_rgba(15,23,42,0.22)]",
          "ring-1 ring-stone-900/[0.04]",
          "dark:border-white/10 dark:bg-gray-900",
          "dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_20px_50px_-12px_rgba(0,0,0,0.55)] dark:ring-white/10",
          { "pointer-events-auto": !plateViewDraggingHappening }
        )}
      >
        <Form idea={idea} deleteAndClose={deleteAndClose} />
        <ShowEditor
          objectKey={idea.id}
          initialContent={idea?.content || ""}
          placeholderText="Clearly state the idea, in your own words.."
          selectionPos={selectionPos}
          surface="paper"
          onPageLinkClick={(idea: Idea) =>
            NavigationHistoryState.set_open_location(idea.location)
          }
          onUpdate={(newContent, newSelectionPos) => {
            commander.exec(
              new Commands.SetIdeaFieldCommand({
                id: idea.id,
                field: "content",
                value: newContent,
                selectionPos: newSelectionPos,
              })
            )
          }}
          recreateWhen={(hub: Hub, recreate): string[] => {
            const k = hub.on(
              "model:idea-updated",
              ({ id, idea, originator, fields }) => {
                if (
                  id === idea.id &&
                  fields.hasOwnProperty("content") &&
                  originator !== "editor"
                ) {
                  recreate()
                }
              }
            )
            return [k]
          }}
          onKeyDown={(event: KeyboardEvent) => {
            if (event.key === "k" && event.metaKey) {
              window.dispatchEvent(new CustomEvent("open-fuzzy-finder", {}))
            }
            if (event.key === "Escape") {
              if (MosaicPageState.escapeShouldCloseEditor)
                NavigationHistoryState.set_open_location(null)
            }
          }}
        />
        <Connections idea={idea} />
        <div className="shrink-0 border-t border-stone-200/70 dark:border-white/10">
          <ShowSource idea={idea} />
          <WithinProjects idea={idea} />
        </div>
      </div>
    </div>
  )
}

export default IdeaPane
